// src/data/translationProgressStorage.js
// Fortschritt der „Freien Übersetzung“:
//  - Wochenzähler: richtig übersetzte Sätze pro Woche (jede Woche zählt für sich)
//  - gelöste Sätze je Richtung (damit zuerst neue Sätze drankommen)
//  - akzeptierte Varianten: Antworten, die per „War auch richtig“ bestätigt wurden
//  - zuletzt gezeigte Sätze (keine direkten Wiederholungen; nur lokal)
//
// Alles liegt im Browser-Speicher (localStorage) und wird, wenn eingeloggt, zusätzlich in
// Supabase gespeichert (Tabellen translation_progress und translation_sentences).
// Schlägt Supabase fehl (z. B. Tabelle fehlt), läuft die Übung lokal weiter.
// Gelöste Sätze und Varianten werden beim Öffnen per syncTranslationSentences aus
// Supabase übernommen und danach lokal gelesen.
import { supabase } from "../supabaseClient";
import { getSessionUser } from "../lib/sessionUser";

const PREFIX = "fluent_translation_";
const PROGRESS_TABLE = "translation_progress";
const SENTENCES_TABLE = "translation_sentences";

function key(kind, profile, langId, ...rest) {
  return [PREFIX + kind, profile || "default", langId || "generic", ...rest].join("_");
}

function readJson(storageKey, fallback) {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(storageKey);
    return raw ? JSON.parse(raw) : fallback;
  } catch (e) {
    console.warn("translationProgressStorage: read failed:", e);
    return fallback;
  }
}

function writeJson(storageKey, value) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(value));
  } catch (e) {
    console.warn("translationProgressStorage: write failed:", e);
  }
}

async function getUserId() {
  try {
    const { data } = await getSessionUser();
    return data?.user?.id || null;
  } catch (e) {
    console.warn("translationProgressStorage: getSessionUser failed:", e);
    return null;
  }
}

// ---------- Wochenzähler ----------

/**
 * Zähler der Woche. Eingeloggt gilt der größere Wert aus Supabase und Browser-Speicher,
 * damit ein fehlgeschlagenes Speichern auf einer Seite nichts zurücksetzt.
 */
export async function loadTranslationWeekCount(profile, langId, pace, weekIndex) {
  const week = weekIndex || 1;
  const storageKey = key("week", profile, langId, pace, `w${week}`);
  const stored = readJson(storageKey, 0);
  const local = typeof stored === "number" && !Number.isNaN(stored) ? stored : 0;

  const userId = await getUserId();
  if (!userId) return local;

  try {
    const { data, error } = await supabase
      .from(PROGRESS_TABLE)
      .select("correct_count")
      .eq("user_id", userId)
      .eq("profile_id", profile)
      .eq("language_id", langId)
      .eq("pace", pace)
      .eq("week_index", week)
      .maybeSingle();
    if (error) throw error;
    const remote = typeof data?.correct_count === "number" ? data.correct_count : 0;
    const count = Math.max(local, remote);
    if (count !== local) writeJson(storageKey, count);
    return count;
  } catch (e) {
    console.warn("translationProgressStorage: load week count failed:", e);
    return local;
  }
}

export async function saveTranslationWeekCount(profile, langId, pace, weekIndex, count) {
  const week = weekIndex || 1;
  writeJson(key("week", profile, langId, pace, `w${week}`), count);

  const userId = await getUserId();
  if (!userId) return;

  const { error } = await supabase.from(PROGRESS_TABLE).upsert(
    {
      user_id: userId,
      profile_id: profile,
      language_id: langId,
      pace,
      week_index: week,
      correct_count: count,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "user_id,profile_id,language_id,pace,week_index" }
  );
  if (error) console.warn("translationProgressStorage: save week count failed:", error);
}

// ---------- gelöste Sätze und akzeptierte Varianten ----------

// Eine Zeile in translation_sentences anlegen oder ändern; nur die übergebenen Felder
// werden überschrieben.
async function upsertSentenceRow(profile, langId, direction, sentenceId, fields) {
  const userId = await getUserId();
  if (!userId) return;

  const { error } = await supabase.from(SENTENCES_TABLE).upsert(
    {
      user_id: userId,
      profile_id: profile,
      language_id: langId,
      sentence_id: sentenceId,
      direction,
      ...fields,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "user_id,profile_id,language_id,sentence_id,direction" }
  );
  if (error) console.warn("translationProgressStorage: save sentence failed:", error);
}

/**
 * Übernimmt gelöste Sätze und akzeptierte Varianten aus Supabase in den Browser-Speicher
 * (Vereinigung, lokal geht nichts verloren). Ohne Login oder bei Fehlern passiert nichts.
 */
export async function syncTranslationSentences(profile, langId) {
  const userId = await getUserId();
  if (!userId) return;

  try {
    const { data, error } = await supabase
      .from(SENTENCES_TABLE)
      .select("sentence_id, direction, solved_at, accepted_variants")
      .eq("user_id", userId)
      .eq("profile_id", profile)
      .eq("language_id", langId);
    if (error) throw error;

    const solvedByDirection = {};
    const variantsKey = key("variants", profile, langId);
    const variants = readJson(variantsKey, {});

    for (const row of data || []) {
      if (row.solved_at) {
        if (!solvedByDirection[row.direction]) {
          solvedByDirection[row.direction] = loadSolvedSentenceIds(profile, langId, row.direction);
        }
        solvedByDirection[row.direction].add(row.sentence_id);
      }
      if (Array.isArray(row.accepted_variants) && row.accepted_variants.length > 0) {
        const id = `${row.sentence_id}:${row.direction}`;
        const list = Array.isArray(variants[id]) ? variants[id] : [];
        variants[id] = [...new Set([...list, ...row.accepted_variants])];
      }
    }

    for (const [direction, solved] of Object.entries(solvedByDirection)) {
      writeJson(key("solved", profile, langId, direction), [...solved]);
    }
    writeJson(variantsKey, variants);
  } catch (e) {
    console.warn("translationProgressStorage: sync sentences failed:", e);
  }
}

export function loadSolvedSentenceIds(profile, langId, direction) {
  const list = readJson(key("solved", profile, langId, direction), []);
  return new Set(Array.isArray(list) ? list : []);
}

export function markSentenceSolved(profile, langId, direction, sentenceId) {
  const solved = loadSolvedSentenceIds(profile, langId, direction);
  if (solved.has(sentenceId)) return;
  solved.add(sentenceId);
  writeJson(key("solved", profile, langId, direction), [...solved]);
  upsertSentenceRow(profile, langId, direction, sentenceId, {
    solved_at: new Date().toISOString(),
  });
}

export function loadAcceptedVariants(profile, langId, direction, sentenceId) {
  const all = readJson(key("variants", profile, langId), {});
  const list = all[`${sentenceId}:${direction}`];
  return Array.isArray(list) ? list : [];
}

export function addAcceptedVariant(profile, langId, direction, sentenceId, answer) {
  const storageKey = key("variants", profile, langId);
  const all = readJson(storageKey, {});
  const id = `${sentenceId}:${direction}`;
  const list = Array.isArray(all[id]) ? all[id] : [];
  if (list.includes(answer)) return;
  all[id] = [...list, answer];
  writeJson(storageKey, all);
  upsertSentenceRow(profile, langId, direction, sentenceId, {
    accepted_variants: all[id],
  });
}

// ---------- zuletzt gezeigte Sätze (nur lokal) ----------

const RECENT_LIMIT = 15;

export function loadRecentSentenceIds(profile, langId) {
  const list = readJson(key("recent", profile, langId), []);
  return Array.isArray(list) ? list : [];
}

export function pushRecentSentenceId(profile, langId, sentenceId) {
  const list = loadRecentSentenceIds(profile, langId).filter((id) => id !== sentenceId);
  list.push(sentenceId);
  const trimmed = list.slice(-RECENT_LIMIT);
  writeJson(key("recent", profile, langId), trimmed);
  return trimmed;
}

// ---------- Zurücksetzen (Developer-Reset im Hauptmenü) ----------

/**
 * Löscht den gesamten Übersetzungs-Fortschritt eines Profils für ein Sprachpaar,
 * lokal (alle Tempi und Wochen) und in Supabase.
 */
export async function resetTranslationProgress(profile, langId) {
  if (typeof window !== "undefined") {
    try {
      const prefixes = ["week", "solved", "variants", "recent"].map(
        (kind) => key(kind, profile, langId)
      );
      Object.keys(window.localStorage)
        .filter((k) => prefixes.some((p) => k === p || k.startsWith(`${p}_`)))
        .forEach((k) => window.localStorage.removeItem(k));
    } catch (e) {
      console.warn("translationProgressStorage: local reset failed:", e);
    }
  }

  const userId = await getUserId();
  if (!userId) return;

  for (const table of [PROGRESS_TABLE, SENTENCES_TABLE]) {
    const { error } = await supabase
      .from(table)
      .delete()
      .eq("user_id", userId)
      .eq("profile_id", profile)
      .eq("language_id", langId);
    if (error) console.warn(`translationProgressStorage: reset ${table} failed:`, error);
  }
}
